import { inject, Injectable } from '@angular/core';
import { NgForm } from '@angular/forms';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { ProcessorService } from '@df/ng/processor.service';
import { CreditCard } from '@df/session/api/credit-cards/credit-card';
import { Customer } from '@df/session/api/customer';
import { Customer as CustomerInterfaces } from '@df/session/customer/customer.interfaces';
import isEqual from 'lodash-es/isEqual';
import { BehaviorSubject, Subject } from 'rxjs';
import { Prefs } from '../prefs';
declare module '@df/core/service/df-event' {
    interface IEventType {
        // customer
        customerDataChange: DfEventConfig<'customerDataChange', CustomerInterfaces.ICustomerData | CustomerInterfaces.ILoggedoutData, void>;
    }
}
@Injectable({
    providedIn: 'root'
})
export class CustomerModel {
    static notLoggedInGroupId = 0;

    /**
     * Gneral customer data
     * @type {ICustomerData}
     */
    data!: CustomerInterfaces.ICustomerData;

    /**
     * True if customer is loaded already
     * @return {boolean} [description]
     */
    loaded = false;
    private loadTime?: number;

    private resetPromise?: Promise<void>;

    /**
     * Saved card tokens
     * @type {CreditCard[]}
     */
    cards!: CreditCard[];

    /**
     * Customer data changes subject
     * @type {Subject}
     */
    obs: CustomerInterfaces.ICustomerObservables;

    /**
     * Load request promise
     * @type {Promise<any>}
     */
    private promise?: Promise<CustomerInterfaces.ILoadResponse>;

    private get lastSeenEmail(): string | undefined {
        return this.localStorageService.get('customerEmail');
    }
    private set lastSeenEmail(value: string | undefined) {
        this.localStorageService.set('customerEmail', value);
    }

    private prefs = inject(Prefs);

    constructor(
        private requestService: RequestService,
        private client: Client,
        private app: App,
        private customer: Customer,
        private localStorageService: LocalStorageService,
        private dfEventService: DfEventService
    ) {
        this.register();

        // initialise obsevables
        this.obs = {
            data: new Subject<CustomerModel>(),
            status: new Subject<boolean>(),
            email$: new BehaviorSubject<string | undefined>(this.lastSeenEmail)
        };

        this.reset();
        ProcessorService.onReady(() => this.load());
    }

    private register() {
        this.customer.register(
            {
                id: 'model',
                listener: 'general',
                account: false
            },
            this
        );
    }

    private loadPromise: Promise<this> | undefined;

    /**
     * Loads customer data
     * @param  {boolean} reload Reload flag
     * @return {promise}
     */
    async load(reload: boolean | number = false): Promise<this> {
        // SSR check
        if (this.client.isPrerender || !this.app.isPlatformBrowser) {
            if (this.resetPromise) {
                await this.resetPromise;
            }
            reload = false;
            this.loaded = true;
            return this;
        }

        const now = Date.now();

        // reload delay check
        if (typeof reload === 'number' && this.loadTime && now - this.loadTime < reload) {
            reload = false;
        }

        // loaded customer processsing
        if (!reload) {
            // NOT this.loaded cause it is set before data deps are loaded
            if (this.customer.isLoaded) {
                return this;
            }
            if (this.loadPromise) {
                return this.loadPromise;
            }
        }

        this.loadTime = now;
        this.loadPromise = (async () => {
            await this.requestService.send(undefined, () => this.requestService.nexus.customer.data());
            return this;
        })();

        return this.loadPromise;
    }

    /**
     * Resets all customer data to plain state
     */
    reset() {
        this.resetPromise = this.setData({
            logged: false,
            reset: true,
            group_id: CustomerModel.notLoggedInGroupId
        });
        this.resetPromise.then(() => {
            this.resetPromise = undefined;
        });
        return this.resetPromise;
    }

    /**
     * Sends save customer data request
     */
    save(
        status: Status,
        data: CustomerInterfaces.ICustomerData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<CustomerInterfaces.ISaveResponse> | void {
        return this.requestService.send<CustomerInterfaces.ISaveResponse>(
            status,
            () => this.requestService.nexus.customer.save(data),
            onSuccess,
            onError
        );
    }

    /**
     * Sends save customer data request
     */
    formSave(
        form: NgForm | undefined,
        status: Status,
        data: CustomerInterfaces.ICustomerData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<CustomerInterfaces.ISaveResponse> | void {
        return this.requestService.submitForm<CustomerInterfaces.ISaveResponse>(
            form,
            status,
            () => this.requestService.nexus.customer.save(data),
            onSuccess,
            onError
        );
    }

    /**
     * Customer data setter
     */
    async setData(data: CustomerInterfaces.ICustomerData | CustomerInterfaces.ILoggedoutData) {
        if (!('group_id' in data) || !(data as CustomerInterfaces.ICustomerData).id) {
            data.group_id = CustomerModel.notLoggedInGroupId;
        }
        if (!isEqual(data, this.data)) {
            await this.dfEventService.dispatch({
                id: 'customerDataChange',
                data: data,
                action: async event => {
                    const isLogged = this.isLoggedIn;
                    this.data = data as CustomerInterfaces.ICustomerData;
                    this.cards = (this.data.cards || []).map(i => new CreditCard(i));
                    this.loaded = !this.data.reset;
                    const prefsInitPromise = this.prefs.init();
                    this.promise = undefined;
                    if (this.loaded) {
                        this.obs.data.next(this);
                        if (isLogged !== this.isLoggedIn) {
                            this.setSeenEmail(this.data.email);
                            this.obs.status.next(this.isLoggedIn);
                        } else if (this.isLoggedIn) {
                            this.setSeenEmail(this.data.email);
                        }
                    }
                    if (prefsInitPromise) {
                        await prefsInitPromise;
                    }
                }
            });
        }
    }

    /**
     * Gets bes email we can find (either from actual data or from any request)
     * @return {string}
     */
    getSeenEmail(): string | undefined {
        return this.obs.email$.value;
    }

    /**
     * Saves seen email (like when customer subscribes to newsletter etc)
     * we will save such email
     * @param {string | undefined} email
     */
    setSeenEmail(email: string | undefined) {
        if (this.isLoggedIn) {
            email = this.data.email;
        }
        if (this.lastSeenEmail !== email) {
            this.lastSeenEmail = email;
        }
        if (this.obs.email$.value !== email) {
            this.obs.email$.next(email);
        }
    }

    /**
     * Checks if customer is logged in
     * @return {boolean}
     */
    get isLoggedIn(): boolean {
        return !!(this.data && this.data.logged);
    }
}

/**
 * Register service in customer
 */
declare module '@df/session/api/customer' {
    interface Customer {
        model: CustomerModel;
    }
}
